import { useMemo } from "react";
import { parseISO } from "date-fns";
import type {
  SavedMapPlotRequestDetail,
  PlotRequestDetailResponse,
  PlotRequestFilterValues,
} from "@/features/map/types";
import { parseAdditionalFields } from "@/features/map/utils/additionalFieldsHelpers";

/**
 * プロット詳細データにフィルター条件を適用するカスタムフック
 */
export const useFilteredPlotDetails = (
  data: PlotRequestDetailResponse | undefined,
  filters: PlotRequestFilterValues
): SavedMapPlotRequestDetail[] => {
  return useMemo((): SavedMapPlotRequestDetail[] => {
    if (!data) return [];

    return data.details.filter((detail) => {
      // 作成日時フィルター（以降）
      if (filters.createdAtFrom) {
        const createdAt = parseISO(detail.createdAt);
        const filterDate = parseISO(filters.createdAtFrom);
        if (createdAt < filterDate) return false;
      }

      // 作成日時フィルター（以前）
      if (filters.createdAtTo) {
        const createdAt = parseISO(detail.createdAt);
        const filterDate = parseISO(filters.createdAtTo);
        if (createdAt > filterDate) return false;
      }

      // 更新日時フィルター（以降）
      if (filters.updatedAtFrom) {
        const updatedAt = parseISO(detail.updatedAt);
        const filterDate = parseISO(filters.updatedAtFrom);
        if (updatedAt < filterDate) return false;
      }

      // 更新日時フィルター（以前）
      if (filters.updatedAtTo) {
        const updatedAt = parseISO(detail.updatedAt);
        const filterDate = parseISO(filters.updatedAtTo);
        if (updatedAt > filterDate) return false;
      }

      // タグフィルター（複数選択可）- 名前で比較
      if (filters.tags.length > 0) {
        const detailTagNames = new Set(detail.tags.map((tag) => tag.name));
        const hasAllTags = filters.tags.every((filterTag) =>
          detailTagNames.has(filterTag.name)
        );
        if (!hasAllTags) return false;
      }

      // ステータスフィルター（複数選択可）
      if (filters.status.length > 0) {
        if (!filters.status.includes(detail.status)) return false;
      }

      // 種別フィルター（複数選択可）
      // NOTE: realEstateTypeがundefinedの場合はデフォルトで土地として扱う
      if (filters.type.length > 0) {
        const detailType = detail.realEstateType ?? "土地";
        if (!filters.type.includes(detailType)) return false;
      }

      // 住居表示フィルター（部分一致）
      if (filters.requestAddress) {
        const address = detail.requestAddress ?? "";
        if (!address.includes(filters.requestAddress)) return false;
      }

      // 所在フィルター（部分一致）
      if (filters.requestLocation) {
        const location = detail.requestLocation ?? "";
        if (!location.includes(filters.requestLocation)) return false;
      }

      // 地番フィルター（部分一致）
      if (filters.requestChiban) {
        const chiban = detail.requestChiban ?? "";
        if (!chiban.includes(filters.requestChiban)) return false;
      }

      // 家屋番号フィルター（部分一致）
      if (filters.requestKaokuNumber) {
        const kaokuNumber = detail.requestKaokuNumber ?? "";
        if (!kaokuNumber.includes(filters.requestKaokuNumber)) return false;
      }

      // 備考フィルター（部分一致）
      if (filters.remarks) {
        const remarks = detail.remarks ?? "";
        if (!remarks.includes(filters.remarks)) return false;
      }

      // 追加項目フィルター（部分一致）
      if (filters.additionalFields) {
        const parsedFields = parseAdditionalFields(detail.additionalFields);

        for (const [key, filterValue] of Object.entries(
          filters.additionalFields
        )) {
          if (!filterValue || filterValue.trim() === "") {
            continue;
          }

          const detailValue = parsedFields[key] ?? "";
          if (!detailValue.includes(filterValue)) {
            return false;
          }
        }
      }

      return true;
    });
  }, [data, filters]);
};
